// ¿A esta empresa le falta hacer el cuestionario de onboarding?
//
// Nace con el alta por link invertida (ADR-2026-09-22-el-alta-por-link-cobra-primero-y-
// pregunta-despues): ahí la empresa se crea sola al registrarse —para que el cliente
// llegue al pago en un paso— y el cuestionario viene DESPUÉS de cobrar. Entre una cosa
// y la otra, la empresa existe pero no tiene rubro de verdad.
//
// El marcador es el RUBRO con un centinela, y no una columna nueva, por dos razones:
//
//  1. `company_profiles.rubro` es NOT NULL en la práctica (0 de 10.829 filas en null al
//     escribir esto) y ninguna empresa tiene este valor, así que el centinela no puede
//     confundirse con un rubro real ni afecta retroactivamente a nadie.
//  2. Una columna nueva no está completa hasta que TODOS los caminos de INSERT la
//     setean, y a este dato lo escriben tres lugares (el cuestionario, el alta por link
//     y el onboarding de Tiendanube). El rubro ya lo escriben los tres.
//
// El backend exige `industry` con al menos un carácter, así que "vacío" no era una
// opción: el centinela es lo que se manda cuando todavía no se preguntó.

/**
 * Rubro que lleva una empresa creada ANTES de preguntarle nada al dueño. Es un valor
 * que el usuario no puede elegir (no está en la lista de rubros) y que el cuestionario
 * pisa apenas termina el paso de categorías.
 */
export const RUBRO_SIN_DEFINIR = 'sin-definir';

/**
 * `true` mientras la empresa siga sin rubro real, o sea mientras le falte pasar por el
 * cuestionario. Es lo que usa la app para devolver ahí a quien lo abandonó a la mitad.
 *
 * Tolerante de más a propósito: `null`, vacío o sólo espacios también cuentan como
 * pendiente. La columna hoy nunca viene así, pero si alguna vez pasa, la respuesta
 * correcta es preguntar el rubro, no dejar a la empresa sin él para siempre.
 */
export function onboardingPendiente(rubro: string | null | undefined): boolean {
  const r = (rubro ?? '').trim().toLowerCase();
  return r === '' || r === RUBRO_SIN_DEFINIR;
}

/**
 * Lo que se muestra donde la pantalla TIENE que decir algo y todavía no hay rubro.
 * Es el texto que ya usaba Configuración → Mis marcas: no se inventa una segunda
 * forma de decir lo mismo.
 */
export const ETIQUETA_SIN_RUBRO = 'Sin rubro';

/**
 * El rubro **tal como se puede mostrar**, o `null` si todavía no hay uno.
 *
 * El centinela es un valor interno, pero vive en `company_profiles.rubro` y en
 * `brand_profiles.rubro`, que son campos que la app PINTA: la tarjeta de marca de la
 * sidebar, el selector de marcas, Mis marcas, el perfil de marca del Estudio de
 * marketing. Y además viaja al prompt del agente. Entre el pago y el final del
 * cuestionario —y para siempre si el update de la marca falla, que es best-effort—
 * el cliente leía "sin-definir" escrito tal cual.
 *
 * Devolver `null` hace que cada pantalla caiga en el estado vacío que YA tiene para
 * "esta marca no cargó el rubro" ("Sin rubro", "Sin definir todavía", la línea que no
 * se dibuja), en vez de sumar un caso especial por pantalla.
 *
 * El valor real sale TAL CUAL —sin trim ni lowercase— porque los gates de
 * `lib/rubro.ts` comparan exacto contra el label del select: normalizar acá cambiaría
 * en silencio qué modos ve una cuenta.
 */
export function rubroVisible(rubro: string | null | undefined): string | null {
  return onboardingPendiente(rubro) ? null : rubro ?? null;
}
